import { useSnapshot } from "valtio";
import { IconButton, Slider, Switch, Tooltip } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslate } from "ra-core";

import useViewerParams from "shared/hooks/useViewerParams";
import settingsState, {
  toggleCrossHair,
  toogleOrientationLabel as toggleOrientationLabel,
  updateRoiOpacity,
  updateWindowLevel,
  resetSettings,
} from "stores/settings";
import { toggleHelp } from "stores/ui";

function ViewerSettings() {
  const { atViewerPage } = useViewerParams();
  const {
    windowLevel,
    roiOpacity,
    showOrientationLabel,
    defaultWindowLevel,
    showCrossHair,
  } = useSnapshot(settingsState);
  const t = useTranslate();

  if (!atViewerPage) {
    return null;
  }

  const handleWidthChange = (_: Event, value: number | number[]) => {
    updateWindowLevel({ width: value as number });
  };

  const handleCenterChange = (_: Event, value: number | number[]) => {
    updateWindowLevel({ center: value as number });
  };

  const handleRoiOpacityChange = (_: Event, value: number | number[]) => {
    updateRoiOpacity(value as number);
  };

  const offset = 1000;

  return (
    <fieldset className="border px-1 rounded-lg inline-block m-2 relative">
      <IconButton
        size="small"
        className="-top-6 -right-4 z-10"
        aria-label="help"
        title="Help"
        onClick={toggleHelp}
        color="secondary"
        sx={{
          position: "absolute",
          backgroundColor: "#121212",
          "&:hover": { backgroundColor: "#121212" },
        }}
      >
        <HelpIcon />
      </IconButton>

      <legend className="px-1.5 flex items-center gap-1">
        <span>{t("settings.title")}</span>
        <Tooltip title="Reset to default">
          <IconButton
            size="small"
            onClick={resetSettings}
            sx={{
              p: 0.2,
              ml: 0.5,
              color: "#aaa",
              "&:hover": { color: "#fff" },
            }}
          >
            <RestartAltIcon fontSize="inherit" style={{ fontSize: "1.1rem" }} />
          </IconButton>
        </Tooltip>
      </legend>

      <form className="grid grid-cols-2 gap-x-2 items-center px-2 py-1">
        <label>{t("settings.field.width")}</label>
        <Slider
          onChange={handleWidthChange}
          min={defaultWindowLevel.width - offset}
          max={defaultWindowLevel.width + offset}
          value={windowLevel.width}
          valueLabelDisplay="auto"
        />

        <label>{t("settings.field.center")}</label>
        <Slider
          onChange={handleCenterChange}
          min={defaultWindowLevel.center - offset}
          max={defaultWindowLevel.center + offset}
          value={windowLevel.center}
          valueLabelDisplay="auto"
        />

        <label>{t("settings.field.roi_opacity")}</label>
        <Slider
          onChange={handleRoiOpacityChange}
          value={roiOpacity}
          valueLabelDisplay="auto"
        />

        <label>{t("settings.field.orientation_labels")}</label>
        <Switch
          checked={showOrientationLabel}
          onChange={toggleOrientationLabel}
        />

        <label>{t("settings.field.3d_localizer")}</label>
        <Switch
          checked={showCrossHair}
          onChange={toggleCrossHair}
          className="block"
        />
      </form>
    </fieldset>
  );
}

export default ViewerSettings;
